import { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'

import { AuthContext } from '@/context/auth-context'
import OsobaService from '@/services/OsobaService'
import PlacowkaService from '@/services/PlacowkaService'
import RozmowaService from '@/services/RozmowaService'
import { getFeedbackText, getSzansa, toApiDate, toDatetimeLocal } from '../../details/rozmowaDetailsHelpers'

const getRelationId = relation => {
	if (!relation) return ''
	if (typeof relation === 'object') return relation?._id || relation?.id || relation?.userid || ''
	return relation
}

const getRelationName = relation => {
	if (!relation || typeof relation !== 'object') return ''
	return relation?.imie_i_nazwisko || relation?.nazwa || relation?.alternative_name || relation?.name || relation?.email || ''
}

const getUserId = user => user?.userid || user?._id || user?.id || ''

export default function useRozmowaEditPage() {
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()
	const { user } = useContext(AuthContext)
	const [form, setForm] = useState({
		id_osoba: '',
		imie_i_nazwisko: '',
		dig_rozmowy: '',
	})
	const [preservedFields, setPreservedFields] = useState({
		feedback: '',
		dig_nastepnej_rozmowy: '',
		szansa: false,
	})
	const [selectedPlacowka, setSelectedPlacowka] = useState(null)
	const [osoby, setOsoby] = useState([])
	const [loading, setLoading] = useState(true)
	const [loadError, setLoadError] = useState(null)
	const [loadingOsoby, setLoadingOsoby] = useState(false)
	const [osobyError, setOsobyError] = useState(null)
	const [submitting, setSubmitting] = useState(false)
	const [rozmowaUserId, setRozmowaUserId] = useState('')

	const handleCancel = useCallback(() => {
		navigate(id ? `/komunikacja/rozmowy/details/${id}` : '/komunikacja/rozmowy')
	}, [id, navigate])

	const handleChange = useCallback((field, value) => {
		setForm(prev => ({
			...prev,
			[field]: value,
		}))
	}, [])

	const loadOsoby = useCallback(async placowkaId => {
		if (!placowkaId) {
			setOsoby([])
			setOsobyError(null)
			return
		}

		setLoadingOsoby(true)
		setOsobyError(null)
		const result = await OsobaService.getByPlacowkaId(placowkaId)

		if (result.error) {
			setOsoby([])
			setOsobyError(result.error)
		} else {
			setOsoby(Array.isArray(result.data) ? result.data : [])
		}

		setLoadingOsoby(false)
	}, [])

	const handleSelectPlacowka = useCallback(placowka => {
		const placowkaId = getRelationId(placowka)

		setSelectedPlacowka(placowka)
		setForm(prev => ({
			...prev,
			id_osoba: '',
			imie_i_nazwisko: '',
		}))
		loadOsoby(placowkaId)
	}, [loadOsoby])

	const handleClearPlacowka = useCallback(() => {
		setSelectedPlacowka(null)
		setOsoby([])
		setOsobyError(null)
		setForm(prev => ({
			...prev,
			id_osoba: '',
			imie_i_nazwisko: '',
		}))
	}, [])

	const handleSelectOsoba = useCallback(osobaId => {
		const selectedOsoba = osoby.find(osoba => getRelationId(osoba) === osobaId)

		setForm(prev => ({
			...prev,
			id_osoba: osobaId,
			imie_i_nazwisko: getRelationName(selectedOsoba),
		}))
	}, [osoby])

	useEffect(() => {
		if (!id) {
			setLoadError(t('conversations.edit.messages.loadFailed'))
			setLoading(false)
			return undefined
		}

		let alive = true

		;(async () => {
			setLoading(true)
			setLoadError(null)

			const result = await RozmowaService.getById(id)
			if (!alive) return

			if (result?.error || !result?.data) {
				setLoadError(result?.error || t('conversations.edit.messages.loadFailed'))
				setLoading(false)
				return
			}

			const rozmowa = result.data
			const placowkaValue = rozmowa?.id_placowka || rozmowa?.placowka
			const placowkaId = getRelationId(placowkaValue)
			let placowkaData = placowkaValue && typeof placowkaValue === 'object' ? placowkaValue : null

			if (placowkaId && !placowkaData) {
				const placowkaResult = await PlacowkaService.getById(placowkaId)
				if (!alive) return
				if (!placowkaResult?.error) {
					placowkaData = placowkaResult?.data || null
				}
			}

			const osoba = rozmowa?.id_osoba || rozmowa?.osoba
			const osobaId = getRelationId(osoba)

			setRozmowaUserId(getRelationId(rozmowa?.id_user || rozmowa?.user))
			setSelectedPlacowka(placowkaData || placowkaValue || null)
			setForm({
				id_osoba: osobaId,
				imie_i_nazwisko: rozmowa?.imie_i_nazwisko || getRelationName(osoba),
				dig_rozmowy: toDatetimeLocal(rozmowa?.dig_rozmowy),
			})
			setPreservedFields({
				feedback: getFeedbackText(rozmowa),
				dig_nastepnej_rozmowy: rozmowa?.dig_nastepnej_rozmowy || '',
				szansa: getSzansa(rozmowa),
			})

			if (placowkaId) {
				await loadOsoby(placowkaId)
			}

			if (alive) setLoading(false)
		})()

		return () => {
			alive = false
		}
	}, [id, loadOsoby, t])

	const handleSubmit = useCallback(async event => {
		event.preventDefault()
		if (submitting || !id) return

		const placowkaId = getRelationId(selectedPlacowka)
		const userId = rozmowaUserId || getUserId(user)

		if (!placowkaId) {
			await Swal.fire(t('conversations.messages.errorTitle'), t('conversations.edit.messages.placowkaRequired'), 'error')
			return
		}

		if (!form.id_osoba) {
			await Swal.fire(t('conversations.messages.errorTitle'), t('conversations.edit.messages.personRequired'), 'error')
			return
		}

		if (!userId) {
			await Swal.fire(t('conversations.messages.errorTitle'), t('conversations.add.messages.userRequired'), 'error')
			return
		}

		setSubmitting(true)

		const payload = {
			id_user: userId,
			id_placowka: placowkaId,
			id_osoba: form.id_osoba,
			imie_i_nazwisko: form.imie_i_nazwisko,
			dig_rozmowy: toApiDate(form.dig_rozmowy),
			feedback: `${preservedFields.feedback || ''}`.slice(0, 5000),
			dig_nastepnej_rozmowy: toApiDate(preservedFields.dig_nastepnej_rozmowy),
			szansa: preservedFields.szansa,
		}

		const result = await RozmowaService.update(id, payload)

		if (!result.success) {
			await Swal.fire(
				t('conversations.messages.errorTitle'),
				result.error || t('conversations.edit.messages.updateFailed'),
				'error'
			)
			setSubmitting(false)
			return
		}

		setSubmitting(false)
		await Swal.fire(
			t('conversations.edit.messages.updatedTitle'),
			t('conversations.edit.messages.updatedSuccess'),
			'success'
		)
		navigate(`/komunikacja/rozmowy/details/${id}`)
	}, [form, id, navigate, preservedFields, rozmowaUserId, selectedPlacowka, submitting, t, user])

	return {
		form,
		handleCancel,
		handleChange,
		handleClearPlacowka,
		handleSelectOsoba,
		handleSelectPlacowka,
		handleSubmit,
		loadError,
		loading,
		loadingOsoby,
		osoby,
		osobyError,
		selectedPlacowka,
		submitting,
	}
}
